import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, Empty, message, Table } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ScatterChart,
  Scatter,
} from 'recharts';
import {
  CloudServerOutlined,
  HourglassOutlined,
  FileImageOutlined,
  RocketOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { makePostCall, STUDY_DATE_FORMAT } from '../../utils/helper';
import dayjs from 'dayjs';

interface PACSServerMetricsProps {
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  selectedModality: string;
}

const PACSServerMetrics: React.FC<PACSServerMetricsProps> = ({ dateRange, selectedModality }) => {
  const [loading, setLoading] = useState(true);
  const [studyMetrics, setStudyMetrics] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const payload = {
        from_date: dateRange[0].format('YYYY-MM-DD 00:00:00'),
        to_date: dateRange[1].format('YYYY-MM-DD 23:59:59'),
      };

      const response = await makePostCall('/get-study-loading-metrics', payload);

      if (response?.data?.success) {
        let data = response.data.data;

        // Filter by modality if selected
        if (selectedModality && selectedModality !== 'ALL') {
          data = data.filter(
            (item: any) => item.modalities && item.modalities.includes(selectedModality)
          );
        }

        // Filter out extreme outliers (> 30 minutes) for better visualization
        data = data.filter((item: any) => parseFloat(item.load_time_minutes) <= 30);

        setStudyMetrics(data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching PACS server metrics:', error);
      message.error('Failed to load PACS server metrics');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedModality]);

  // Calculate aggregate statistics
  const calculateAggregates = () => {
    if (!studyMetrics.length) return null;

    const loadTimes = studyMetrics.map((item: any) => parseFloat(item.load_time_minutes));
    const sizes = studyMetrics.map((item: any) => parseFloat(item.study_size_mb));
    const instances = studyMetrics.map((item: any) => parseInt(item.instance_count));

    return {
      totalStudies: studyMetrics.length,
      avgLoadTime: (loadTimes.reduce((a, b) => a + b, 0) / loadTimes.length).toFixed(2),
      minLoadTime: Math.min(...loadTimes).toFixed(2),
      maxLoadTime: Math.max(...loadTimes).toFixed(2),
      avgSize: (sizes.reduce((a, b) => a + b, 0) / sizes.length).toFixed(2),
      totalSize: sizes.reduce((a, b) => a + b, 0).toFixed(2),
      avgInstances: Math.round(instances.reduce((a, b) => a + b, 0) / instances.length),
      totalInstances: instances.reduce((a, b) => a + b, 0),
    };
  };

  // Prepare data for study size vs load time scatter plot
  const prepareSizeVsTimeData = () => {
    return studyMetrics.map((item: any) => ({
      size: parseFloat(item.study_size_mb),
      loadTime: parseFloat(item.load_time_minutes),
      instances: parseInt(item.instance_count),
      modality: item.modalities,
    }));
  };

  // Prepare data for load time distribution
  const prepareLoadTimeDistribution = () => {
    const buckets = [
      { range: '0-1 min', min: 0, max: 1, count: 0 },
      { range: '1-2 min', min: 1, max: 2, count: 0 },
      { range: '2-5 min', min: 2, max: 5, count: 0 },
      { range: '5-10 min', min: 5, max: 10, count: 0 },
      { range: '10-20 min', min: 10, max: 20, count: 0 },
      { range: '20+ min', min: 20, max: Infinity, count: 0 },
    ];

    studyMetrics.forEach((item: any) => {
      const loadTime = parseFloat(item.load_time_minutes);
      const bucket = buckets.find((b) => loadTime >= b.min && loadTime < b.max);
      if (bucket) bucket.count++;
    });

    return buckets;
  };


  // Prepare modality breakdown
  const prepareModalityBreakdown = () => {
    const modalityMap: any = {};

    studyMetrics.forEach((item: any) => {
      const modalities = item.modalities?.split(',') || ['Unknown'];
      modalities.forEach((mod: string) => {
        const modality = mod.trim();
        if (!modalityMap[modality]) {
          modalityMap[modality] = {
            count: 0,
            totalLoadTime: 0,
            totalSize: 0,
            totalInstances: 0,
          };
        }
        modalityMap[modality].count++;
        modalityMap[modality].totalLoadTime += parseFloat(item.load_time_minutes);
        modalityMap[modality].totalSize += parseFloat(item.study_size_mb);
        modalityMap[modality].totalInstances += parseInt(item.instance_count);
      });
    });

    return Object.keys(modalityMap).map((modality) => ({
      modality,
      count: modalityMap[modality].count,
      avgLoadTime: (modalityMap[modality].totalLoadTime / modalityMap[modality].count).toFixed(2),
      avgSize: (modalityMap[modality].totalSize / modalityMap[modality].count).toFixed(2),
      avgInstances: Math.round(modalityMap[modality].totalInstances / modalityMap[modality].count),
    }));
  };

  const aggregates = calculateAggregates();
  const sizeVsTimeData = prepareSizeVsTimeData();
  const loadTimeDistribution = prepareLoadTimeDistribution();
  const modalityBreakdown = prepareModalityBreakdown();

  // Table columns for detailed view
  const columns = [
    {
      title: 'Study Date',
      dataIndex: 'study_date',
      key: 'study_date',
      width: 100,
      render: (text: string) => dayjs(text, 'YYYYMMDD').format(STUDY_DATE_FORMAT),
    },
    {
      title: 'Modality',
      dataIndex: 'modalities',
      key: 'modalities',
      width: 80,
    },
    {
      title: 'Series',
      dataIndex: 'series_count',
      key: 'series_count',
      width: 70,
    },
    {
      title: 'Instances',
      dataIndex: 'instance_count',
      key: 'instance_count',
      width: 90,
    },
    {
      title: 'Size (MB)',
      dataIndex: 'study_size_mb',
      key: 'study_size_mb',
      width: 90,
      render: (text: string) => parseFloat(text).toFixed(2),
      sorter: (a: any, b: any) => parseFloat(a.study_size_mb) - parseFloat(b.study_size_mb),
    },
    {
      title: 'Load Time',
      dataIndex: 'load_time_minutes',
      key: 'load_time_minutes',
      width: 100,
      render: (text: string) => `${parseFloat(text).toFixed(2)} min`,
      sorter: (a: any, b: any) => parseFloat(a.load_time_minutes) - parseFloat(b.load_time_minutes),
    },
    {
      title: 'Description',
      dataIndex: 'study_desc',
      key: 'study_desc',
      ellipsis: true,
    },
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="Loading PACS server metrics..." />
      </div>
    );
  }

  if (!studyMetrics.length) {
    return (
      <div className="no-data-container">
        <Empty description="No PACS server data available for the selected filters" />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Summary Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="Total Studies Loaded"
              value={aggregates?.totalStudies}
              prefix={<CloudServerOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="Avg Load Time"
              value={aggregates?.avgLoadTime}
              suffix="min"
              prefix={<HourglassOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="Avg Study Size"
              value={aggregates?.avgSize}
              suffix="MB"
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="Total Data Transferred"
              value={aggregates?.totalSize}
              suffix="MB"
              prefix={<RocketOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Study Size vs Load Time Scatter Plot */}
      <Card title="Study Size vs Load Time (Server Performance)" className="chart-card">
        <ResponsiveContainer width="100%" height={350}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="size"
              name="Size"
              label={{ value: 'Study Size (MB)', position: 'insideBottomRight', offset: -10 }}
            />
            <YAxis
              type="number"
              dataKey="loadTime"
              name="Load Time"
              label={{ value: 'Load Time (minutes)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Legend />
            <Scatter name="Study Upload Times" data={sizeVsTimeData} fill="#82ca9d" />
          </ScatterChart>
        </ResponsiveContainer>
        <div style={{ marginTop: 12, fontSize: 12, color: '#666', textAlign: 'center' }}>
          This chart shows the relationship between study size and server load time.
          Efficient upload performance even with large studies.
        </div>
      </Card>

      {/* Load Time Distribution */}
      <Card title="Load Time Distribution" className="chart-card" style={{ marginTop: 20 }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={loadTimeDistribution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="range" />
            <YAxis label={{ value: 'Number of Studies', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#8884d8" name="Study Count" />
          </BarChart>
        </ResponsiveContainer>
        <div style={{ marginTop: 12, fontSize: 12, color: '#666', textAlign: 'center' }}>
          Distribution of studies by their upload/load time to the PACS server.
        </div>
      </Card>

      {/* Modality Breakdown */}
      <Card title="Performance by Modality" className="chart-card" style={{ marginTop: 20 }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={modalityBreakdown}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="modality" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="avgLoadTime" fill="#8884d8" name="Avg Load Time (min)" />
            <Bar dataKey="avgSize" fill="#82ca9d" name="Avg Size (MB)" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Detailed Table */}
      <Card title="Detailed Study Metrics" className="chart-card" style={{ marginTop: 20 }}>
        <Table
          columns={columns}
          dataSource={studyMetrics}
          rowKey="study_iuid"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} studies`,
          }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </Card>

      {/* Performance Summary */}
      <Card
        title="Server Performance Summary"
        className="chart-card"
        style={{ marginTop: 20, background: '#e6f7ff', borderColor: '#91d5ff' }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="Fastest Upload"
              value={aggregates?.minLoadTime}
              suffix="min"
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Average Instances/Study"
              value={aggregates?.avgInstances}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Instances Processed"
              value={aggregates?.totalInstances}
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 20, padding: 12, background: 'white', borderRadius: 4 }}>
          <p style={{ margin: 0, fontSize: 14, color: '#333' }}>
            <strong>Server Analysis:</strong> The PACS server has processed{' '}
            <strong>{aggregates?.totalStudies}</strong> studies with an average load time of{' '}
            <strong>{aggregates?.avgLoadTime} minutes</strong>. The system efficiently handles studies
            of varying sizes (avg <strong>{aggregates?.avgSize} MB</strong>) demonstrating robust
            server-side performance and network efficiency superior to conventional PACS solutions.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default PACSServerMetrics;
